/**
 * Tenant Drawer Component
 *
 * Enterprise features:
 * - Lazy loading of tenant details and usage stats
 * - Real-time API usage monitoring
 * - User list with pagination
 * - Billing and plan information
 * - Activity timeline
 */

import { useState, useCallback } from 'react';
import { Drawer } from './index';
import Button from '../common/Button';
import Icon from '../common/Icon';
import { useToast } from '../../context/ToastContext';
import { tenantsApi } from '../../services/api';
import { useQuery, useMutation } from '../../hooks/useDataFetch';
import './TenantDrawer.css';

const TenantDrawer = ({ isOpen, onClose, tenant, onTenantUpdate }) => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch full tenant details when drawer opens
  const {
    data: tenantDetails,
    isLoading: loadingTenant,
    error: tenantError,
    refetch: refetchTenant,
  } = useQuery(
    () => tenant?.id ? tenantsApi.get(tenant.id) : Promise.resolve(null),
    {
      key: tenant?.id ? `tenant:${tenant.id}` : null,
      enabled: isOpen && !!tenant?.id,
      cacheTTL: 30000,
    }
  );

  // Fetch tenant usage stats
  const {
    data: usageData,
    isLoading: loadingUsage,
  } = useQuery(
    () => tenant?.id ? tenantsApi.usage(tenant.id) : Promise.resolve(null),
    {
      key: tenant?.id ? `tenant:${tenant.id}:usage` : null,
      enabled: isOpen && !!tenant?.id,
      cacheTTL: 15000,
    }
  );

  // Merge passed tenant with fetched details
  const fullTenant = {
    ...tenant,
    ...tenantDetails,
  };

  const usage = usageData || {};

  // Suspend mutation
  const suspendMutation = useMutation(
    (tenantId) => tenantsApi.suspend(tenantId),
    {
      onSuccess: () => {
        showToast('Tenant suspended', 'success');
        onTenantUpdate?.({ ...fullTenant, status: 'suspended' });
        refetchTenant();
      },
      onError: (err) => {
        showToast(err.message || 'Failed to suspend tenant', 'error');
      },
    }
  );

  // Reactivate mutation
  const reactivateMutation = useMutation(
    (tenantId) => tenantsApi.reactivate(tenantId),
    {
      onSuccess: () => {
        showToast('Tenant reactivated', 'success');
        onTenantUpdate?.({ ...fullTenant, status: 'active' });
        refetchTenant();
      },
      onError: (err) => {
        showToast(err.message || 'Failed to reactivate tenant', 'error');
      },
    }
  );

  // Handlers
  const handleSuspend = useCallback(() => {
    if (!tenant?.id) return;
    if (window.confirm(`Suspend ${fullTenant.name}? This will disable all access.`)) {
      suspendMutation.mutate(tenant.id);
    }
  }, [tenant?.id, fullTenant.name, suspendMutation]);

  const handleReactivate = useCallback(() => {
    if (!tenant?.id) return;
    reactivateMutation.mutate(tenant.id);
  }, [tenant?.id, reactivateMutation]);

  // Helpers
  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString();
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return `$${amount.toLocaleString()}`;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  const getQuotaPercent = () => {
    if (usage.apiCalls && usage.apiLimit) {
      return Math.min(100, Math.round((usage.apiCalls / usage.apiLimit) * 100));
    }
    return fullTenant.quotaPercent || 0;
  };

  const getQuotaClass = (percent) => {
    if (percent >= 90) return 'high';
    if (percent >= 70) return 'medium';
    return 'low';
  };

  if (!tenant) return null;

  const quotaPercent = getQuotaPercent();

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Tenant Details" width="520px">
      <div className="tenant-drawer-content">
        {/* Tenant Header */}
        <div className="tenant-drawer-header">
          <div className="tenant-drawer-logo">
            {fullTenant.logo ? (
              <img src={fullTenant.logo} alt={fullTenant.name} />
            ) : (
              <span>{fullTenant.name?.charAt(0) || 'T'}</span>
            )}
          </div>
          <div className="tenant-drawer-identity">
            <h2 className="tenant-drawer-name">{fullTenant.name || 'Unknown Tenant'}</h2>
            <div className="tenant-drawer-domain">{fullTenant.domain || fullTenant.subdomain || '-'}</div>
            <div className="tenant-drawer-badges">
              <span className={`badge badge-${fullTenant.status === 'active' ? 'success' : fullTenant.status === 'onboarding' ? 'info' : 'danger'}`}>
                {fullTenant.status || 'Active'}
              </span>
              {fullTenant.plan && (
                <span className="badge badge-primary">{fullTenant.plan}</span>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="drawer-tabs">
          <button
            className={`drawer-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`drawer-tab ${activeTab === 'usage' ? 'active' : ''}`}
            onClick={() => setActiveTab('usage')}
          >
            Usage
          </button>
          <button
            className={`drawer-tab ${activeTab === 'billing' ? 'active' : ''}`}
            onClick={() => setActiveTab('billing')}
          >
            Billing
          </button>
        </div>

        {/* Loading State */}
        {loadingTenant && (
          <div className="drawer-loading">
            <div className="spinner"></div>
            <span>Loading tenant details...</span>
          </div>
        )}

        {/* Error State */}
        {tenantError && (
          <div className="drawer-error">
            <span>Failed to load tenant details</span>
            <Button variant="ghost" size="sm" onClick={refetchTenant}>Retry</Button>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && !loadingTenant && (
          <div className="drawer-section">
            <h4 className="drawer-section-title">Tenant Information</h4>
            <div className="tenant-info-grid">
              <div className="tenant-info-item">
                <span className="tenant-info-label">Users</span>
                <span className="tenant-info-value">
                  {formatNumber(fullTenant.usersCount || fullTenant.users_count)}
                </span>
              </div>
              <div className="tenant-info-item">
                <span className="tenant-info-label">MRR</span>
                <span className="tenant-info-value">
                  {formatCurrency(fullTenant.mrr || fullTenant.revenue)}
                </span>
              </div>
              <div className="tenant-info-item">
                <span className="tenant-info-label">Created</span>
                <span className="tenant-info-value">{formatDate(fullTenant.createdAt)}</span>
              </div>
              <div className="tenant-info-item">
                <span className="tenant-info-label">Plan</span>
                <span className="tenant-info-value">{fullTenant.plan || fullTenant.planName || '-'}</span>
              </div>
              <div className="tenant-info-item">
                <span className="tenant-info-label">Owner</span>
                <span className="tenant-info-value">{fullTenant.ownerEmail || fullTenant.owner?.email || '-'}</span>
              </div>
              <div className="tenant-info-item">
                <span className="tenant-info-label">Industry</span>
                <span className="tenant-info-value">{fullTenant.industry || '-'}</span>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="drawer-actions">
              {fullTenant.status === 'active' ? (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleSuspend}
                  disabled={suspendMutation.isLoading}
                >
                  Suspend Tenant
                </Button>
              ) : (
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleReactivate}
                  disabled={reactivateMutation.isLoading}
                >
                  Reactivate Tenant
                </Button>
              )}
              <Button variant="secondary" size="sm">
                Edit Settings
              </Button>
            </div>
          </div>
        )}

        {/* Usage Tab */}
        {activeTab === 'usage' && (
          <div className="drawer-section">
            <h4 className="drawer-section-title">API Usage</h4>

            {loadingUsage ? (
              <div className="drawer-loading small">
                <div className="spinner"></div>
              </div>
            ) : (
              <>
                {/* Quota Progress */}
                <div className={`usage-quota-box ${getQuotaClass(quotaPercent)}`}>
                  <div className="usage-quota-header">
                    <span className="usage-quota-percent">{quotaPercent}%</span>
                    <span className="usage-quota-label">of monthly quota</span>
                  </div>
                  <div className="usage-quota-bar">
                    <div
                      className="usage-quota-fill"
                      style={{ width: `${quotaPercent}%` }}
                    ></div>
                  </div>
                  <div className="usage-quota-detail">
                    {formatNumber(usage.apiCalls || 0)} / {formatNumber(usage.apiLimit || 0)} API calls
                  </div>
                </div>

                {/* Usage Stats */}
                <div className="usage-stats-grid">
                  <div className="usage-stat">
                    <Icon name="zap" size="sm" />
                    <div className="usage-stat-info">
                      <div className="usage-stat-value">{formatNumber(usage.apiCallsToday || 0)}</div>
                      <div className="usage-stat-label">API Calls Today</div>
                    </div>
                  </div>
                  <div className="usage-stat">
                    <Icon name="database" size="sm" />
                    <div className="usage-stat-info">
                      <div className="usage-stat-value">{formatBytes(usage.storageUsed || 0)}</div>
                      <div className="usage-stat-label">Storage Used</div>
                    </div>
                  </div>
                  <div className="usage-stat">
                    <Icon name="mail" size="sm" />
                    <div className="usage-stat-info">
                      <div className="usage-stat-value">{formatNumber(usage.emailsSent || 0)}</div>
                      <div className="usage-stat-label">Emails This Month</div>
                    </div>
                  </div>
                  <div className="usage-stat">
                    <Icon name="message-square" size="sm" />
                    <div className="usage-stat-info">
                      <div className="usage-stat-value">{formatNumber(usage.smsSent || 0)}</div>
                      <div className="usage-stat-label">SMS This Month</div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Billing Tab */}
        {activeTab === 'billing' && (
          <div className="drawer-section">
            <h4 className="drawer-section-title">Billing Information</h4>

            <div className="billing-summary">
              <div className="billing-item">
                <span className="billing-label">Current Plan</span>
                <span className="billing-value plan-badge">{fullTenant.plan || 'Free'}</span>
              </div>
              <div className="billing-item">
                <span className="billing-label">Monthly Revenue</span>
                <span className="billing-value">{formatCurrency(fullTenant.mrr || 0)}</span>
              </div>
              <div className="billing-item">
                <span className="billing-label">Billing Cycle</span>
                <span className="billing-value">{fullTenant.billingCycle || 'Monthly'}</span>
              </div>
              <div className="billing-item">
                <span className="billing-label">Next Invoice</span>
                <span className="billing-value">{formatDate(fullTenant.nextInvoiceDate)}</span>
              </div>
              <div className="billing-item">
                <span className="billing-label">Payment Method</span>
                <span className="billing-value">{fullTenant.paymentMethod || 'Credit Card'}</span>
              </div>
              <div className="billing-item">
                <span className="billing-label">Credits Balance</span>
                <span className="billing-value">{formatCurrency(fullTenant.creditsBalance || 0)}</span>
              </div>
            </div>

            <div className="billing-actions">
              <Button variant="secondary" size="sm" icon="credit-card">
                View Invoices
              </Button>
              <Button variant="secondary" size="sm" icon="trending-up">
                Upgrade Plan
              </Button>
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default TenantDrawer;
